import { Alert, Button } from "antd";
import type { InvoiceVo, ReceivePayment } from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE, S_INVOICE_STATE } from "@zhihaoscm/meta";
import { dateTimeFormat, fmtMoney, TABLE_COLUMN_WIDTH } from "@zhihaoscm/utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DetailHeader, Table, type TableColumnProps } from "zhihao-ui-react";
import { fileService } from "@/api/fileService";
import { tradeInvoiceService } from "@/api/tradeInvoiceService";
import { DetailJson } from "@/components/common";
import "./invoice.css";

/** 发票详情的金额和时间沿用 Vue fmtMoney/dateTimeFormat，空值保持 --。 */
const money = (value?: number, unit = "") => fmtMoney(value, unit);
const dateTime = (value?: string | Date) => dateTimeFormat(value) || "--";

/** 发票详情按旧版 DetailJson 的三个连续信息块展示。 */
export function InvoiceDetailPage() {
    const { id = "" } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<InvoiceVo>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            setDetail(await tradeInvoiceService.findById(id));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    const preview = async (fileId?: number) => {
        if (!fileId) return;
        const file = await fileService.getFileById(fileId);
        window.open(file.path, "_blank");
    };
    // 旧 Vue 详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !detail)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "发票详情加载失败" : "缺少发票编号"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const invoice = detail.invoice;
    const columns: TableColumnProps<ReceivePayment>[] = [
        {
            type: "index",
            label: "序号",
            minWidth: 68,
        },
        { label: "收款编号", prop: "id", minWidth: 148 },
        {
            label: "费用类型",
            prop: "paymentType",
            minWidth: TABLE_COLUMN_WIDTH.WORD4,
            enum: RECEIVE_PAYMENT_TYPE.options
        },
        {
            label: "支付金额(元)",
            prop: "amount",
            formatter: row => money(row.amount)
        },
        { label: "支付时间", prop: "payTime", width: 180, formatter: row => dateTime(row.payTime) },
        {
            label: "开票金额(元)",
            prop: "invoicedAmount",
            width: 300,
            formatter: row => money(row.invoicedAmount)
        }
    ];
    return (
        <section className="invoice-detail-page">
            <div className="invoice-detail-header">
                <DetailHeader
                    title="发票详情"
                    leftInfos={[{ label: "发票编号", value: id }]}
                    rightInfos={[{ label: "创建时间", value: dateTime(invoice.createdTime) }]}
                    stateDesc={S_INVOICE_STATE.getIntroByCode(invoice.state) ?? "--"}
                />
            </div>
            <DetailJson
                data={detail}
                columns={[
                    {
                        sectionTitle: "订单编号",
                        list: [
                        {
                            label: "订单编号",
                            render: () => (
                                <Button
                                    type="link"
                                    onClick={() => navigate(`/orderCenter/myOrder/detail/${detail.order.id}`)}
                                >
                                    {detail.order.id}
                                </Button>
                            )
                        },
                            { label: "实际收款金额", value: money(detail.order.actualReceivedAmount, "元") },
                            { label: "未开票金额", value: money(detail.order.uninvoicedAmount, "元") },
                            { label: "已开票金额", value: money(detail.order.invoicedAmount, "元") }
                        ]
                    },
                    {
                        sectionTitle: (
                            <div className="invoice-detail-title">
                                <span className="invoice-detail-title__text">发票信息</span>
                                <span className="invoice-detail-title__amount">开票总金额{money(invoice.amount, "元")}</span>
                            </div>
                        ),
                        list: [
                            {
                                render: () => <Table columns={columns} data={detail.receivePayments} pagination={false} rowKey="id" />,
                                show: Boolean(detail.receivePayments?.length),
                                span: 4
                            },
                        {
                            label: "发票单据",
                            render: () => (
                                <Button type="link" onClick={() => void preview(invoice.fileId)}>
                                    查看/下载
                                </Button>
                            )
                        }
                        ]
                    },
                    {
                        sectionTitle: "作废信息",
                        show: Boolean(invoice.invalidReason),
                        list: [
                            { label: "作废时间", value: dateTime(invoice.invoiceDate) },
                            { key: "invoice.invalidReason", label: "作废原因" },
                            {
                                label: "作废文件",
                                render: () => (
                                    <Button type="link" onClick={() => void preview(invoice.invalidFileId)}>
                                        查看/下载
                                    </Button>
                                )
                            }
                        ]
                    }
                ]}
            />
            <div className="invoice-detail-actions operation-fixed-btn">
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
        </section>
    );
}
